import React, { useEffect, useRef, useState } from "react"
import Link from "next/link"

import {
  useAcceptTestSetAutoAddSuggestionTransport,
  useRejectTestSetAutoAddSuggestionTransport,
  useTestSetAutoAddPendingTransport,
  useTestSetAutoAddSummaryTransport,
} from "@/test-set/transport"
import { Button, Input, Skeleton, Textarea, toast } from "@coval/ui"
import { ExternalLink, Plus, X } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"
import type { TestSetAutoAddPendingItem } from "@/types_api/backend/transport/list_test_set_auto_add_pending_response"
import { formatRelativeTimestamp } from "@/utils/formatting/format-timestamp"

import { EVENTS, useEventDispatcher } from "../../event-bus"
import { normalizeEditableTestCases } from "../../utils/testSetEditorState"

interface TestSetAutoAddPendingReviewProps {
  testSetId: string
}

/**
 * Auto-added test cases waiting for a reviewer. Accepting writes the edited
 * expected behaviors onto the test case and activates it; rejecting removes it.
 */
export const TestSetAutoAddPendingReview: React.FC<
  TestSetAutoAddPendingReviewProps
> = ({ testSetId }) => {
  const pendingQuery = useTestSetAutoAddPendingTransport(testSetId)
  const summaryQuery = useTestSetAutoAddSummaryTransport(testSetId)

  if (pendingQuery.error) {
    return (
      <div className="flex items-center gap-3 text-sm text-coval-danger-text">
        <span>Could not load pending suggestions.</span>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void pendingQuery.refetch()}
        >
          Retry
        </Button>
      </div>
    )
  }
  if (pendingQuery.isLoading) {
    return <Skeleton className="h-16 w-full rounded-md" />
  }

  const items = pendingQuery.data ?? []
  const counts = summaryQuery.data?.counts_by_status ?? {}
  const evaluated = Object.entries(counts)
    .filter(([status]) => status !== "QUEUED")
    .reduce((total, [, count]) => total + count, 0)

  return (
    <div className="flex flex-col gap-3" data-testid="auto-add-pending-review">
      {summaryQuery.error ? (
        <div
          role="alert"
          className="flex items-center gap-3 text-sm text-coval-danger-text"
        >
          <span>Could not refresh auto-add counts.</span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void summaryQuery.refetch()}
          >
            Retry counts
          </Button>
        </div>
      ) : !summaryQuery.data ? (
        <Skeleton className="h-4 w-full rounded-md" />
      ) : (
        <p className="text-xs text-coval-text-muted">
          {items.length} to review · {counts.COVERED ?? 0} already covered ·{" "}
          {(counts.SKIPPED_BUDGET ?? 0) +
            (counts.SKIPPED_CAP ?? 0) +
            (counts.SKIPPED_FULL ?? 0)}{" "}
          skipped · {counts.FAILED ?? 0} failed · {evaluated} evaluated
        </p>
      )}
      {(counts.SKIPPED_FULL ?? 0) > 0 ? (
        <p className="text-xs text-coval-text-muted">
          This test set has more cases than one coverage check can compare, so
          new conversations are skipped until cases are removed.
        </p>
      ) : null}
      {items.length === 0 ? (
        <p className="text-sm text-coval-text-muted">
          Nothing waiting for review.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((item) => (
            <PendingSuggestionCard key={item.evaluation_id} item={item} />
          ))}
        </ul>
      )}
    </div>
  )
}

interface PendingSuggestionCardProps {
  item: TestSetAutoAddPendingItem
}

const PendingSuggestionCard: React.FC<PendingSuggestionCardProps> = ({
  item,
}) => {
  const queryParams = useQueryParams()
  const dispatch = useEventDispatcher()
  const accept = useAcceptTestSetAutoAddSuggestionTransport()
  const reject = useRejectTestSetAutoAddSuggestionTransport()
  const [scenario, setScenario] = useState(item.suggested_scenario ?? "")
  const [description, setDescription] = useState(
    item.suggested_description ?? "",
  )
  const [behaviors, setBehaviors] = useState<string[]>(
    item.suggested_expected_behaviors,
  )
  const isBusy = accept.isPending || reject.isPending
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const updateBehavior = (index: number, value: string) =>
    setBehaviors((previous) =>
      previous.map((behavior, i) => (i === index ? value : behavior)),
    )

  const handleAccept = async () => {
    try {
      const result = await accept.mutateAsync({
        evaluation_id: item.evaluation_id,
        expected_behaviors: behaviors
          .map((behavior) => behavior.trim())
          .filter(Boolean),
        input_str:
          scenario.trim() && scenario.trim() !== item.suggested_scenario
            ? scenario.trim()
            : null,
        description:
          description.trim() !== (item.suggested_description ?? "")
            ? description.trim()
            : null,
      })
      if (!mounted.current) {
        return
      }
      const [testCase] = normalizeEditableTestCases([
        {
          id: result.test_case_id,
          input_str: scenario.trim(),
          description: description.trim(),
          expected_behaviors: behaviors
            .map((behavior) => behavior.trim())
            .filter(Boolean),
          input_type: "SCENARIO",
        },
      ])
      dispatch(EVENTS.PERSISTED_TEST_CASE_ADD, { testCase })
      toast.success({ title: "Test case added to the test set" })
    } catch (error: unknown) {
      toast.error({
        title: "Could not accept the suggestion",
        error,
        fallback: "Your suggestion could not be saved. Please try again.",
      })
    }
  }

  const handleReject = async () => {
    try {
      await reject.mutateAsync({ evaluation_id: item.evaluation_id })
    } catch (error: unknown) {
      toast.danger({
        title: "Could not reject the suggestion",
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  return (
    <li
      className="flex flex-col gap-4 rounded-lg border border-coval-border bg-surface-0 p-5"
      data-testid="auto-add-pending-item"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Input
            aria-label="Suggested description"
            placeholder="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <Button asChild variant="secondary">
          <Link
            href={queryParams.getHref(
              `/conversations/results/${encodeURIComponent(item.simulation_output_id)}`,
            )}
          >
            Source conversation
            <ExternalLink aria-hidden="true" className="h-3 w-3" />
          </Link>
        </Button>
      </div>
      <Textarea
        aria-label="Suggested scenario"
        rows={3}
        value={scenario}
        onChange={(event) => setScenario(event.target.value)}
      />
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-coval-text-muted">
          Expected behaviors
        </span>
        {behaviors.map((behavior, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              aria-label={`Expected behavior ${index + 1}`}
              value={behavior}
              onChange={(event) => updateBehavior(index, event.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              icon
              tooltip={`Remove expected behavior ${index + 1}`}
              onClick={() =>
                setBehaviors((previous) =>
                  previous.filter((_, i) => i !== index),
                )
              }
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          className="self-start"
          onClick={() => setBehaviors((previous) => [...previous, ""])}
        >
          <Plus className="h-4 w-4" />
          Add expected behavior
        </Button>
      </div>
      {item.reason && (
        <p className="text-xs text-coval-text-muted">Reason: {item.reason}</p>
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-coval-text-muted">
          Suggested {formatRelativeTimestamp(item.created_at)}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="danger"
            disabled={isBusy}
            loading={reject.isPending}
            onClick={() => void handleReject()}
          >
            Reject
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={isBusy || scenario.trim() === ""}
            loading={accept.isPending}
            onClick={() => void handleAccept()}
          >
            Accept
          </Button>
        </div>
      </div>
    </li>
  )
}
